// Conveyors: the first thing in this world that moves an object without anybody
// having decided to.
//
// Everything else that changes tiles is moved by an agent -- a shove, a Momentum
// cast, a bind link dragging it, a mob walking. A belt moves what is standing on
// it because it is a machine and it is switched on, and it goes on doing that
// while the player is doing something else entirely. That is the whole reason it
// is worth building: it puts a second clock in the room that the caster does not
// own, and it is the only way to get a boulder onto a pad, or yourself across a
// gap, without spending the move that does it.
//
// **Three networks now, and this is the third.** Water traces a channel, Charge
// spreads across conductive terrain, and a conveyor run is a connected component
// of belts joined by orthogonal adjacency. Deliberately built out of
// the same parts rather than beside them:
//
//   - **A run is a path, and every heading falls out of it.** Nothing about a
//     belt says which way it runs: each segment points at its successor along
//     the run and the last one carries on the way it was going, so four tiles
//     bending east into south read east, east, south, south and reverse to west,
//     west, west, north. Corners need no rule, an author cannot write a run down
//     inconsistently, and reversing is one bit rather than four headings --
//     because a path read backwards is still a path.
//   - **Rust breaks a run exactly where it breaks a circuit.** A corroded belt
//     is not in any run (`isBeltLive`), which is `queries.isConductive`'s one
//     line restated for a machine instead of a conductor -- and because the
//     seized sprite is what `rustFamily` dresses it in, the break is drawn at the
//     tile where the break is. Entropy is therefore a real lever on a conveyor
//     without one line here mentioning Entropy.
//   - **Power is somebody else's mechanism entirely.** The console is a Gate
//     Terminal in a different housing -- `conduit.conductive`, a self-reading
//     `conduit.feed`, `capacity: 1` -- so cabling, a Charged Font, a breaker
//     thrown by hand, a `Bind`·`Charge` wire and a rusted floor tile all reach it
//     with nothing here knowing they exist. What this module adds is one extra
//     condition, and it is a *physical* one: a console powers the runs it is
//     orthogonally touching. Wire it perfectly and stand it one tile off the belt
//     and it does nothing.
//
// **What moves, and what is furniture.** A belt carries the objects on its tile
// that are not `walkable` -- which is the line the renderer already draws between
// a floor fixture and a thing standing on the floor (see `createObjectSprite`'s
// depth rule). A pressure pad, a length of cabling and a signpost stay where they
// were put; a boulder, a crate and a mob ride. The caster rides too, through the
// same `movePlayerTo` a step goes through, so a belt that carries them onto a
// live Metal Floor shocks them exactly as walking onto it would.
import { Facing, ObjectDef, RoomDef } from './types';
import { DIRECTIONS, DIRECTION_OFFSETS } from './constants';
import { PhysicsConfig, DEFAULT_PHYSICS } from './physics';
import { SimEvents } from './events';
import { WorldState } from './state';
import { canObjectEnter, doorAt, isConduitCharged } from './queries';
import { CONVEYOR, rustStage } from './rust';
import { applyStagePresentation } from './presentation';
import { moveObjectTo, movePlayerTo } from './casts';

/** Every texture a conveyor can ever wear: the belt's four headings in each of
 * its two corrosion palettes, and the console's sound/corroded pair crossed with
 * the lit look `conduit.feed` gives it. Exported so the renderer generates
 * exactly this set and the object catalog lists exactly this set, rather than
 * either carrying its own copy. */
export const CONVEYOR_TEXTURES: string[] = [
  ...DIRECTIONS.map((dir) => `object-conveyor-${dir}`),
  ...DIRECTIONS.map((dir) => `object-conveyor-rusted-${dir}`),
];

export const CONVEYOR_CONSOLE_TEXTURES: string[] = ['object-conveyor-console', 'object-conveyor-console-lit', 'object-conveyor-console-rusted'];

/** Whether this object is a length of belt rather than a console.
 *
 * Carrying the cluster is what makes it part of a conveyor; not being the
 * control is what makes it belt. A bare `conveyor: {}` is the whole of what a
 * belt has to say about itself, because everything else -- which way it runs,
 * how it draws -- is a fact about the run it is in rather than about the tile. */
export function isBelt(obj: ObjectDef): boolean {
  return obj.conveyor !== undefined && obj.conveyor.console !== true;
}

/** Whether this object is a length of belt that still works -- it is belt, and
 * it has not seized.
 *
 * The exact shape `queries.isConductiveSubstance` has for the charge walk, and for
 * the same reason: what joins a run and what carries a load have to be one
 * question, or a rusted segment would break the machine somewhere other than
 * where the picture says it did. */
export function isBeltLive(obj: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS): boolean {
  if (!isBelt(obj)) return false;
  return !obj.rust || rustStage(obj, physics) !== 'rusted';
}

/** Leftmost, then topmost. The tie-break every choice in this module makes, so
 * that a run's shape decides its heading and nothing else does -- there is no
 * randomness anywhere in this simulation, and a recorded take has to replay to
 * the same world.
 *
 * **Leftmost first, not topmost**, which is the opposite of ordinary reading
 * order and was chosen against it deliberately. It decides which end of a run is
 * the head, and the two rules disagree on exactly the shape an author draws most
 * -- a line that runs east and then turns. Topmost-first makes the *corner's*
 * far end the head, so a run laid left-to-right flows right-to-left the moment it
 * bends upward, and painting one more tile at the top flips every arrow in it.
 * Leftmost-first reads an L the way a person does: a belt from (1,3) east to
 * (3,3) and then south to (3,4) runs east, east, south, south, and reversed runs
 * west, west, west, north. */
function runOrder(a: ObjectDef, b: ObjectDef): number {
  return a.x - b.x || a.y - b.y || a.id.localeCompare(b.id);
}

/** Whether this object is a console -- the control, not the belt. */
export function isConveyorConsole(obj: ObjectDef): boolean {
  return obj.conveyor?.console === true;
}

/** Every working belt joined to `belt`, itself included: the run.
 *
 * A plain connected component over orthogonal adjacency, which is
 * `queries.chargeNetwork`'s first edge kind with the other two left out on
 * purpose. A belt is not conductive terrain and nothing rides a wire, so
 * co-location and `chargeBind` have nothing to say about a machine made of
 * rollers -- the run is where the belt physically goes, and that is all it is. */
export function conveyorRun(room: RoomDef, belt: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS): ObjectDef[] {
  if (!isBeltLive(belt, physics)) return [];
  const run: ObjectDef[] = [belt];
  const seen = new Set<string>([belt.id]);
  for (let i = 0; i < run.length; i++) {
    const here = run[i];
    for (const dir of DIRECTIONS) {
      const offset = DIRECTION_OFFSETS[dir];
      for (const other of room.objects) {
        if (seen.has(other.id) || other.x !== here.x + offset.x || other.y !== here.y + offset.y) continue;
        if (!isBeltLive(other, physics)) continue;
        seen.add(other.id);
        run.push(other);
      }
    }
  }
  return run;
}

/** A run's segments in the order the load travels through them.
 *
 * A run is a *set* -- a connected component -- and a direction of travel needs a
 * *sequence*, so this is where one becomes the other. Three shapes, and each is
 * a real thing an author will draw:
 *
 * - **A line** (two ends) is walked from one end to the other, and **the head is
 *   the end the console is nearest** (2026-08-16). The drive is where the motor
 *   is, so a run carries *away* from its console -- which is a physical fact an
 *   author can see across the room rather than a convention they have to learn,
 *   and which is stable while a run is being drawn: extending the far end cannot
 *   move the console. `conveyor.reversed` is how an author says they meant the
 *   other way anyway.
 *
 *   A run with **no console** (nothing drives it, so nothing moves -- but it
 *   still has to be *drawn*) falls back to `runOrder`, leftmost then topmost,
 *   which reads an L the way a person does. So does a console sitting exactly
 *   halfway along, where "nearest end" has no answer.
 * - **A loop** (no ends at all) is walked from its first tile in `runOrder`,
 *   toward whichever of its two neighbours comes first in the same order. A
 *   closed circuit of belt is a good thing to be able to build and needs no
 *   special case beyond having no end to start at. The console does **not**
 *   orient one, deliberately: a cycle has no end for the drive to be nearest,
 *   and both ways round lead equally away from the motor, so there is nothing
 *   for it to decide. `reversed` still flips which way it goes.
 * - **A branch** (a tee, a cross) has no single path through it. It is walked
 *   anyway, preferring the first unvisited neighbour in `runOrder`, which
 *   produces a sensible spine and leaves the spurs trailing off it -- and
 *   `worldIssues` warns, because a branched conveyor is far more often a
 *   painting mistake than a design.
 *
 * Reversing is reversing the sequence, which is why it is one bit rather than
 * four headings: a path read backwards is still a path, and every segment's
 * heading falls out of it. */
export function conveyorPath(room: RoomDef, belt: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS): ObjectDef[] {
  const run = conveyorRun(room, belt, physics);
  if (run.length <= 1) return run;

  const neighbours = new Map<string, ObjectDef[]>();
  for (const segment of run) {
    const near = run
      .filter((other) => other.id !== segment.id && Math.abs(other.x - segment.x) + Math.abs(other.y - segment.y) === 1)
      .sort(runOrder);
    neighbours.set(segment.id, near);
  }

  const ends = run.filter((segment) => (neighbours.get(segment.id) ?? []).length <= 1).sort(runOrder);
  // A loop has no ends; start at its first tile in run order and let the
  // neighbour ordering below pick which way round it goes.
  const head = ends[0] ?? [...run].sort(runOrder)[0];

  const path: ObjectDef[] = [head];
  const seen = new Set<string>([head.id]);
  for (;;) {
    const next = (neighbours.get(path[path.length - 1].id) ?? []).find((candidate) => !seen.has(candidate.id));
    if (!next) break;
    seen.add(next.id);
    path.push(next);
  }
  // A branch leaves segments unvisited -- the spurs off the spine. They are
  // appended in run order so every segment still gets a heading and nothing
  // silently stops working; the warning is `worldIssues`'s job, not this one's.
  for (const segment of [...run].sort(runOrder)) if (!seen.has(segment.id)) path.push(segment);

  // **The drive end.** The walk above started at whichever end `runOrder` picked,
  // which is a fact about coordinates; where the console is, is a fact about the
  // machine. So if the console is nearer the *other* end, the path is turned
  // round -- a run carries away from its motor.
  //
  // Measured along the path rather than by straight-line distance, because a run
  // that doubles back can put a console physically close to a segment that is a
  // long way round the belt from it, and it is the belt's length that the load
  // travels. A tie (a console exactly halfway) keeps `runOrder`'s answer, and so
  // does a run with no console at all -- which still has to be drawn even though
  // nothing will ever move it. A loop is left alone: it has no end for a drive to
  // be nearest, and both ways round lead equally away.
  const oriented = ends.length > 0 && drivenFromFarEnd(room, path) ? [...path].reverse() : path;

  // The run's orientation, read from one fixed representative rather than from
  // whichever tile this walk happened to start at (see `ConveyorDef.reversed`).
  const representative = [...run].sort(runOrder)[0];
  return representative.conveyor?.reversed ? [...oriented].reverse() : oriented;
}

/** Whether the console driving this path is nearer its last segment than its
 * first -- in which case the path is the wrong way round and the caller turns it.
 *
 * The console is picked in `runOrder` where a run has more than one, and so is
 * the segment it touches where it touches more than one, for the reason every
 * choice in this module is made that way: two consoles on one run is an unusual
 * thing to build and a coin flip between them would be a run whose direction
 * nobody could predict. */
function drivenFromFarEnd(room: RoomDef, path: ObjectDef[]): boolean {
  const onPath = new Map(path.map((segment, index) => [`${segment.x},${segment.y}`, index]));
  let driven: number | undefined;
  for (const console of [...room.objects].filter(isConveyorConsole).sort(runOrder)) {
    for (const dir of DIRECTIONS) {
      const offset = DIRECTION_OFFSETS[dir];
      const index = onPath.get(`${console.x + offset.x},${console.y + offset.y}`);
      if (index !== undefined && (driven === undefined || index < driven)) driven = index;
    }
    if (driven !== undefined) break;
  }
  return driven !== undefined && driven > path.length - 1 - driven;
}

/** Which way every live belt in the room currently carries.
 *
 * **This is the answer, and `conveyor.direction` is a copy of it.** Every rule
 * that acts on a belt calls this rather than reading the field, so a belt that
 * has not been settled since it was placed still behaves correctly; the field
 * exists because presentation needs a heading resolvable from the object alone
 * (`rust.ts`'s `CONVEYOR` table is keyed by it).
 *
 * Each segment points at its successor. **The last one keeps the heading it was
 * entered with**, which is what carries a load off the end of the run rather
 * than stranding it on the final tile -- so a line running east that turns south
 * reads east, east, south, south, and reversed reads west, west, west, north.
 *
 * A lone segment with no neighbours at all has no path to take a heading from,
 * and gets `right` (or `left` reversed) -- a degenerate case with no good answer,
 * and the one place a heading is a convention rather than a consequence. */
export function conveyorDirections(room: RoomDef, physics: PhysicsConfig = DEFAULT_PHYSICS): Map<string, Facing> {
  const headings = new Map<string, Facing>();
  const done = new Set<string>();

  for (const candidate of [...room.objects].sort(runOrder)) {
    if (done.has(candidate.id) || !isBeltLive(candidate, physics)) continue;
    const path = conveyorPath(room, candidate, physics);
    for (const segment of path) done.add(segment.id);

    if (path.length === 1) {
      headings.set(path[0].id, path[0].conveyor?.reversed ? 'left' : 'right');
      continue;
    }
    // A closed circuit has no tail: its last segment's successor is the tile the
    // walk started from, so it points there and the loop goes round rather than
    // carrying its load off into the wall. Detected by the two ends of the walk
    // being adjacent, which for a run of three or more can only mean it closed --
    // a shorter run's ends are its two tiles, and a belt that pointed back at the
    // tile behind it would shuffle its load forever.
    const closes = path.length > 2 && stepHeading(path[path.length - 1], path[0]) !== undefined;
    let previous: Facing = 'right';
    for (let i = 0; i < path.length; i++) {
      const next = path[i + 1] ?? (closes ? path[0] : undefined);
      // A tail with no successor carries on the way it was going, which is what
      // delivers a load off the end of a run instead of stranding it.
      const heading: Facing = next ? stepHeading(path[i], next) ?? previous : previous;
      headings.set(path[i].id, heading);
      previous = heading;
    }
  }
  return headings;
}

/** The heading from one tile to an orthogonally adjacent one, or `undefined` for
 * a pair that is not adjacent -- which happens exactly once, where a branch's
 * spurs are appended to the end of a path (see `conveyorPath`). Such a segment
 * inherits rather than pointing at something it does not touch. */
function stepHeading(from: ObjectDef, to: ObjectDef): Facing | undefined {
  if (to.x === from.x + 1 && to.y === from.y) return 'right';
  if (to.x === from.x - 1 && to.y === from.y) return 'left';
  if (to.y === from.y + 1 && to.x === from.x) return 'down';
  if (to.y === from.y - 1 && to.x === from.x) return 'up';
  return undefined;
}

/** The consoles orthogonally touching any belt in this run.
 *
 * Adjacency rather than a named id, deliberately, and it is the same argument
 * `conduit.feed`'s missing `standingPillarId` makes for a consumer: an authored
 * reference would dangle the moment either object is stamped into a fresh room,
 * and a console that has to be *put against* the belt it drives is a fact the
 * player can see from across the room. */
function consolesFor(room: RoomDef, run: ObjectDef[]): ObjectDef[] {
  const found: ObjectDef[] = [];
  const seen = new Set<string>();
  for (const belt of run) {
    for (const dir of DIRECTIONS) {
      const offset = DIRECTION_OFFSETS[dir];
      for (const obj of room.objects) {
        if (seen.has(obj.id) || !isConveyorConsole(obj)) continue;
        if (obj.x !== belt.x + offset.x || obj.y !== belt.y + offset.y) continue;
        seen.add(obj.id);
        found.push(obj);
      }
    }
  }
  return found;
}

/** Whether this belt is currently turning: it is a working belt, and at least
 * one console touching its run reads charged.
 *
 * Recomputed live from the room every time, never stored -- the same shape
 * `isPadSatisfied` and `isConduitCharged` already have, and what makes throwing a
 * breaker halfway across the room stop the belt in the same settle, with no
 * conveyor-shaped rule anywhere in the Charge system. */
export function isConveyorRunning(room: RoomDef, belt: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS): boolean {
  if (!isBeltLive(belt, physics)) return false;
  return consolesFor(room, conveyorRun(room, belt, physics)).some((console) => isConduitCharged(room, console, physics));
}

/** Every belt in the room that is currently turning, in one pass.
 *
 * The same answer `isConveyorRunning` gives for each belt, and it exists only
 * because asking that question per belt is quadratic in a way this one is not: a
 * run of ten belts would walk its own run ten times and ask its console for a
 * network potential ten times, and `networkPotential` is itself a graph walk that
 * re-traces the room's water on every call. Here each run is walked once and each
 * console asked once, per tick.
 *
 * Kept beside the single-belt reading rather than replacing it: the editor's
 * overlays and the tests ask about one object, and two readings that could
 * disagree would be one too many -- so this is the same two calls, batched, and
 * `conveyor.test.ts` pins the two against each other. */
function runningBelts(room: RoomDef, physics: PhysicsConfig): ObjectDef[] {
  const running: ObjectDef[] = [];
  const assigned = new Set<string>();
  for (const belt of room.objects) {
    if (assigned.has(belt.id) || !isBeltLive(belt, physics)) continue;
    const run = conveyorRun(room, belt, physics);
    for (const segment of run) assigned.add(segment.id);
    if (consolesFor(room, run).some((console) => isConduitCharged(room, console, physics))) running.push(...run);
  }
  return running;
}

/** Throws a console's lever: every belt in every run it touches reverses.
 *
 * Called from `sim/interact.ts`, which is the only way a player reaches it --
 * carrying `conveyor.console` is the whole signal that Interact throws it, with
 * no `interact` field saying so, exactly as carrying `breaker` is for a switch.
 *
 * **Works dead**, and that is the point rather than an oversight: the lever is
 * mechanical, so a console on a line with no power still turns the rollers over
 * and the arrows still flip. A player who has worked out which way the belt will
 * run before switching it on has understood the machine, and making them power it
 * to find out would be charging them for the knowledge.
 *
 * Returns how many segments actually flipped, which is what tells a caller
 * whether anything happened at all -- a console standing against nothing throws
 * with a clunk and changes no world state. */
export function reverseConveyor(room: RoomDef, console: ObjectDef, physics: PhysicsConfig = DEFAULT_PHYSICS): number {
  if (!isConveyorConsole(console)) return 0;
  const flipped = new Set<string>();
  for (const dir of DIRECTIONS) {
    const offset = DIRECTION_OFFSETS[dir];
    const neighbour = room.objects.find((o) => o.x === console.x + offset.x && o.y === console.y + offset.y && isBeltLive(o, physics));
    if (!neighbour) continue;
    const run = conveyorRun(room, neighbour, physics);
    if (run.some((belt) => flipped.has(belt.id))) continue;
    // One bit for the whole run, read from a fixed representative and written to
    // every segment -- a machine cannot half-reverse, and writing it everywhere
    // is what stops a later edit from leaving the run arguing with itself. The
    // *headings* are not touched at all: they are derived from the path, and
    // reversing the path is the whole of reversing the belt.
    const representative = [...run].sort(runOrder)[0];
    const next = !representative.conveyor?.reversed;
    for (const belt of run) {
      flipped.add(belt.id);
      belt.conveyor!.reversed = next;
    }
  }
  return flipped.size;
}

/** The declared system's own body (see settle.ts): re-derives every belt's
 * heading from the path it sits in, and dresses it accordingly.
 *
 * **A belt joined this pass when its heading stopped being authored**
 * (2026-08-16). Until then a belt's whole look was two fields on the object --
 * its own `conveyor.direction` crossed with its own corrosion stage -- so
 * `rustFamily` could resolve it and `settleRust` dressed a belt exactly as it
 * dresses a Metal Block, with no system of its own. Deriving the heading from
 * the *run* moved half of that answer off the object and into the room, which is
 * precisely the cable's situation, so a belt is now in `ownsItsOwnLook` beside a
 * cable and a breaker, and this is where it gets dressed.
 *
 * The heading is written back as well as drawn, because `rust.ts`'s `CONVEYOR`
 * table is keyed by it and an object's texture has to be resolvable from the
 * object -- but nothing *acts* on the written value: `stepConveyors` asks
 * `conveyorDirections` directly, so a belt placed and not yet settled still
 * carries correctly. */
export function settleConveyors(world: WorldState, out: SimEvents): void {
  const room = world.rooms[world.player.roomId];
  if (!room) return;
  const headings = conveyorDirections(room, world.physics);
  for (const obj of room.objects) {
    if (!isBelt(obj)) continue;
    // A seized belt is in no run and so has no derived heading -- it keeps
    // whichever it last had, which is the honest picture: the rollers stopped,
    // they did not swing round.
    const heading = headings.get(obj.id) ?? obj.conveyor!.direction ?? 'right';
    obj.conveyor!.direction = heading;
    applyStagePresentation(obj, CONVEYOR[heading][rustStage(obj, world.physics)], out);
  }
}

/** One thing a belt is about to carry, and where to.
 *
 * `player` rather than an `ObjectDef` for the caster, because the two are moved
 * through different choke points (`movePlayerTo` bills hazards and drags binds
 * from the caster; `moveObjectTo` drags them from an object) and a belt must not
 * become a third way to move either. */
interface Ride {
  rider: ObjectDef | 'player';
  from: { x: number; y: number };
  to: { x: number; y: number };
  direction: Facing;
}

/** Whether a belt carries this object.
 *
 * `walkable` is the whole test, and it is the line the world already draws: a
 * walkable object is a floor fixture (a pressure pad, a length of cabling, a
 * signpost, another belt), and everything else is a thing standing on the floor.
 * So a pad under a belt stays put and a boulder on top of it rides, with no
 * per-object opt-in to author and no list of carriable kinds to keep in step
 * with the catalog.
 *
 * A `bind`-linked object is refused for exactly the reason `queries.canPush` and
 * `advanceMobs` refuse one: its movement is already entirely dictated by the
 * locus it follows, so carrying it would double-count against the link. That is
 * also where the interesting case comes from rather than being lost to it -- bind
 * something to a rider and the belt moves *both*, through
 * `propagateBoundMovement`, including whatever is standing off the belt
 * entirely. */
function isCarried(obj: ObjectDef, belt: ObjectDef): boolean {
  return obj.id !== belt.id && !obj.walkable && !obj.bind;
}

/** Where a belt would put what is on it, or null when it may not put it there at
 * all.
 *
 * **Only the reasons that cannot change during a step are checked here.** A
 * doorway is one: crossing it is a room transition rather than a step, which is
 * why every other object mover refuses one too, and a conveyor that fed the
 * caster through a locked door would be a way past it. Whether the tile is
 * *occupied* is deliberately not asked until the ride is applied -- it is the
 * one thing a step changes as it goes, and asking early is what makes a queue of
 * three boulders move only its front one, each of the others reading a tile
 * that is about to be vacated. */
function rideDestination(room: RoomDef, belt: ObjectDef, direction: Facing): { x: number; y: number } | null {
  const offset = DIRECTION_OFFSETS[direction];
  const to = { x: belt.x + offset.x, y: belt.y + offset.y };
  return doorAt(room, to.x, to.y) ? null : to;
}

/** Advances every conveyor in the room the caster is standing in by `dtMs`, and
 * returns how many tiles were actually carried.
 *
 * Only that room, matching the rule the rest of `tick` follows: an unvisited room
 * does not decay, does not conduct and does not thaw, and a belt turning in a
 * room nobody has seen is the same claim.
 *
 * **One accumulator for every belt in the world, unlike a mob's.** A mob keeps
 * its own `sinceStepMs` precisely so that two of them cannot be forced into one
 * phase -- creatures on a shared heartbeat read as a chorus line. Machinery is
 * the opposite: every run in a room is driven off the same power, and belts that
 * stepped a third of a second apart would be a rhythm the player has to watch
 * rather than count. So the accumulator is `world.sinceConveyorMs`, beside the
 * two ambient rules, and every belt in the room moves in the same instant.
 *
 * Loops rather than stepping at most once, as everything else in `tick` does, so
 * a single large skip behaves like the many small steps it stands for.
 *
 * **Downstream first.** Within one step the rides are applied in order of how far
 * along their own heading they already are, so a queue of three boulders on one
 * belt advances as a queue instead of the back of it jamming against the front.
 * Ties are broken by id, because this simulation has no randomness anywhere and a
 * recorded take has to replay to the same world. Anything the ordering does not
 * resolve -- two belts pointing into the same tile -- resolves by arrival: the
 * first ride applied takes the tile and the second finds it occupied and jams,
 * which is what two conveyors feeding one gap should do. */
export function advanceConveyors(world: WorldState, dtMs: number, out: SimEvents): number {
  const room = world.rooms[world.player.roomId];
  const stepMs = world.physics.conveyor.stepMs;
  if (!room || dtMs <= 0 || stepMs <= 0) return 0;
  // Nothing to accumulate toward in a room with no conveyor in it, which is
  // every room in the world today bar the ones authored for it.
  if (!room.objects.some((o) => o.conveyor)) return 0;

  world.sinceConveyorMs += dtMs;
  let carried = 0;
  while (world.sinceConveyorMs >= stepMs) {
    world.sinceConveyorMs -= stepMs;
    carried += stepConveyors(world, out);
  }
  return carried;
}

/** One step of every powered belt in the room. See `advanceConveyors` for the
 * ordering rule and why it is the one it is. */
function stepConveyors(world: WorldState, out: SimEvents): number {
  const room = world.rooms[world.player.roomId];
  if (!room) return 0;
  const physics = world.physics;

  // Which belts are running is decided once, up front, against the room as it
  // stands at the start of the step -- not re-asked between rides. A belt whose
  // load is carried onto a pressure pad may open a gate, and a gate opening may
  // (through nothing today, but through nothing forbidden either) change what is
  // powered; resolving that inside a step would make the second half of a run
  // behave differently from the first for reasons no player could see.
  const running = runningBelts(room, physics);
  if (running.length === 0) return 0;

  // Asked directly rather than read off `conveyor.direction`, which is a copy
  // this pass may be running ahead of: a belt placed by an editor and stepped
  // before anything settled would otherwise carry the heading it was stamped
  // with instead of the one its run gives it.
  const headings = conveyorDirections(room, physics);

  const rides: Ride[] = [];
  for (const belt of running) {
    const direction = headings.get(belt.id);
    if (!direction) continue;
    const to = rideDestination(room, belt, direction);
    if (!to) continue;
    const from = { x: belt.x, y: belt.y };
    if (world.player.x === belt.x && world.player.y === belt.y) rides.push({ rider: 'player', from, to, direction });
    for (const obj of room.objects) {
      if (obj.x !== belt.x || obj.y !== belt.y || !isCarried(obj, belt)) continue;
      rides.push({ rider: obj, from, to, direction });
    }
  }

  // How far along its own heading a ride already sits, so that sorting
  // descending puts the most downstream one first. Two riders on different
  // headings are not really comparable, which is fine: what has to be right is
  // the order *within* one straight stretch, and any consistent total order gives
  // that.
  const progress = (ride: Ride) => {
    const offset = DIRECTION_OFFSETS[ride.direction];
    return ride.from.x * offset.x + ride.from.y * offset.y;
  };
  const rideId = (ride: Ride) => (ride.rider === 'player' ? '' : ride.rider.id);
  rides.sort((a, b) => progress(b) - progress(a) || rideId(a).localeCompare(rideId(b)));

  let carried = 0;
  for (const ride of rides) {
    // Occupancy is asked here rather than when the ride was planned, and that is
    // what makes a queue a queue: the ride ahead of this one may have vacated
    // the tile (so this one follows it) or taken it (so this one jams). Both
    // riders read the room as it is at the moment they move, which is the only
    // reading that can be right for both.
    //
    // `canObjectEnter` rather than `isWalkable`, for the caster as much as for a
    // boulder: being carried is not a deliberate walk, and 2026-08-11's "only
    // the caster climbs" rule is precisely that everything which is not a chosen
    // step uses the object predicate. A belt running at a vine-covered cliff
    // jams against it rather than posting a boulder up it.
    if (ride.rider === 'player') {
      // The caster may have been carried already this step by the belt they were
      // standing on -- they can only be on one -- but they may also have been
      // dragged off it by a bind link between planning and now.
      if (world.player.x !== ride.from.x || world.player.y !== ride.from.y) continue;
      if (!canObjectEnter(room, ride.to.x, ride.to.y, physics)) continue;
      movePlayerTo(world, ride.to.x, ride.to.y, out);
    } else {
      const obj = ride.rider;
      if (obj.x !== ride.from.x || obj.y !== ride.from.y) continue;
      if (!canObjectEnter(room, ride.to.x, ride.to.y, physics)) continue;
      // The caster is not one of `room.objects`, so `canObjectEnter` cannot know
      // about them -- refused here for the same reason a mob refuses their tile:
      // a belt delivers a boulder *beside* you, and what standing under one
      // costs is a question for a mechanism that has actually been designed,
      // not something to smuggle in as an overlap.
      if (world.player.x === ride.to.x && world.player.y === ride.to.y) continue;
      moveObjectTo(world, obj, ride.to.x, ride.to.y, out);
    }
    carried++;
  }
  return carried;
}
